import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Hospital, ShieldCheck, Clock, FileText, Users, BadgeCheck, Workflow, MessageCircle, ArrowRight,
  Building2, Brain, Stethoscope, Zap
} from "lucide-react";

const BENEFITS = [
  {
    icon: Brain,
    title: "NIMHANS & AIIMS-trained specialists",
    body: "Every neurologist + neurosurgeon on our panel is MCI/NMC-verified. Most are trained at NIMHANS Bengaluru or AIIMS Delhi.",
  },
  {
    icon: Clock,
    title: "48-hour turnaround (12 h urgent)",
    body: "Hard SLA with auto-escalation. T-6h alerts go to the assigned specialist + our ops team — your case never sits idle.",
  },
  {
    icon: ShieldCheck,
    title: "TMG-2020 compliant reports",
    body: "Structured neuro reports with the doctor's MCI/NMC registration number, alma mater, and digital signature. Schedule-X drugs auto-flagged.",
  },
  {
    icon: Users,
    title: "Hospital-only case feed",
    body: "Your team sees only your hospital's submissions. Per-hospital MRN tracking, referring-clinician attribution, monthly spend report.",
  },
  {
    icon: FileText,
    title: "DICOM + PDF upload, no email chains",
    body: "Drag-and-drop multi-file upload (up to 1 GB per file, resumable — and secure DICOM upload links for multi-GB CT/MRI studies). DICOM, PDF, JPEG, MP4 — all native. No more emailing CD images.",
  },
  {
    icon: BadgeCheck,
    title: "Patient verification on prescriptions",
    body: "Every digital Rx carries an 8-character verification ID. Pharmacists and family can confirm authenticity at /verify-rx without exposing meds.",
  },
];

const STEPS = [
  { num: 1, title: "Apply",        body: "30-second hospital signup with org details, address, and a primary contact. Approved within 24 hours." },
  { num: 2, title: "Submit cases", body: "One-form-per-patient or upload a batch. Pick written opinion (₹1,000) or video consult (₹2,000) and a specialist (or let us route)." },
  { num: 3, title: "Get reports",  body: "Structured PDF reports delivered to the hospital dashboard. Patient-friendly + clinician-friendly views." },
];

const FAQS = [
  {
    q: "Who owns the patient relationship?",
    a: "You do. We never contact the patient directly — patient consent for the second opinion is collected by the hospital under our B2B MSA. Reports are delivered into your hospital dashboard, not to the patient.",
  },
  {
    q: "What does it cost?",
    a: "Flat per-case pricing: ₹1,000 written second opinion (48 h) or ₹2,000 video consultation (24 h). No setup fee, no monthly minimum. Volume discounts available for 50+ cases/month (write in).",
  },
  {
    q: "How is patient data handled?",
    a: "All scans + reports are encrypted at rest on Indian-region object storage. Access is restricted to the assigned specialist + your hospital's authorised users. DPDP-compliant audit trail.",
  },
  {
    q: "What if the specialist misses the SLA?",
    a: "Automatic refund. Our refund loop processes Cashfree-backed refunds the moment a case crosses its SLA without an opinion. Plus T-6h escalation alerts so it almost never gets there.",
  },
  {
    q: "Can we integrate with our HIS / EMR?",
    a: "Not yet via API — that's our Q3 roadmap. Today every case carries your hospital's internal MRN so you can reconcile manually.",
  },
];

export default function ForHospitals() {
  useEffect(() => {
    document.title = "NeuroOpinion for Hospitals — Neurology Second Opinion Network";
    // Inject FAQ JSON-LD for SEO rich results
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "for-hospitals-faq";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(ld);
    return () => { document.getElementById("for-hospitals-faq")?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-teal-300 text-[11px] font-semibold uppercase tracking-wider border border-white/15">
              <Hospital className="w-3.5 h-3.5"/> For hospitals &amp; clinics
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05]">
              Add a NIMHANS-grade <span className="text-teal-300">neurology second opinion</span> layer to your hospital.
            </h1>
            <p className="text-slate-300 mt-5 text-base lg:text-lg max-w-xl">
              Refer complex brain &amp; spine cases to MCI/NMC-verified specialists without your team
              chasing doctors on WhatsApp. Reports in 48 hours, 12 hours for urgent. Per-hospital
              case feed, MRN tracking, audit trail.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/hospital/signup">
                <Button className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold h-11 px-6" data-testid="forhosp-cta-apply">
                  Apply for B2B access <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </Link>
              <a href="https://wa.me/918317674160?text=Hi%2C%20we%27re%20a%20hospital%20interested%20in%20the%20NeuroOpinion%20B2B%20program.">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 h-11 px-6" data-testid="forhosp-cta-whatsapp">
                  <MessageCircle className="w-4 h-4 mr-2"/> Talk to us on WhatsApp
                </Button>
              </a>
            </div>
            <div className="mt-7 flex items-center gap-6 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-teal-300"/> MCI/NMC verified panel</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-teal-300"/> DPDP compliant</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-teal-300"/> 48 h SLA</span>
            </div>
          </div>
          <div className="hidden lg:block">
            <Card className="bg-white/5 border-white/10 text-white p-6 backdrop-blur">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-300">Sample case view</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <Stat label="Open cases" value="34"/>
                <Stat label="In review" value="12"/>
                <Stat label="Completed (30d)" value="221"/>
                <Stat label="Avg TAT" value="36 h"/>
              </div>
              <div className="mt-4 space-y-2">
                {[
                  { name: "MRN-99821 · Ramesh K.",      tag: "Urgent · 12 h", colour: "bg-rose-500/20 text-rose-200 border-rose-400/40" },
                  { name: "MRN-99844 · Anita Sharma",   tag: "Standard · 48 h", colour: "bg-blue-500/20 text-blue-200 border-blue-400/40" },
                  { name: "MRN-99861 · S. Pillai",      tag: "Completed", colour: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" },
                ].map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-xs rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                    <span className="font-mono text-slate-200">{r.name}</span>
                    <span className={`px-1.5 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${r.colour}`}>{r.tag}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-4 py-16 lg:py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center">Why partner with NeuroOpinion?</h2>
        <p className="text-slate-600 text-center mt-3 max-w-2xl mx-auto">
          We are not a marketplace — we are a curated, NIMHANS-grade neurology panel + a hospital-grade workflow.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b) => (
            <Card key={b.title} className="p-5 border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <span className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                <b.icon className="w-5 h-5"/>
              </span>
              <h3 className="font-heading font-semibold text-slate-900 mt-3">{b.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{b.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 text-center flex items-center justify-center gap-3">
            <Workflow className="w-7 h-7 text-blue-700"/> How it works
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.num} className="text-center md:text-left" data-testid={`forhosp-step-${s.num}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white font-heading font-bold text-lg shadow-md">
                  {s.num}
                </div>
                <h3 className="font-heading font-semibold text-slate-900 mt-3 text-lg">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/hospital/signup">
              <Button className="bg-blue-700 hover:bg-blue-800 h-11 px-6" data-testid="forhosp-step-cta">
                Start your application <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Use-cases / who's it for */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Built for the hospital teams who refer the most.</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Building2, label: "Multi-specialty hospitals", body: "When your in-house neurologist is on leave or pre-booked, refer urgent cases to a NIMHANS-trained panel in &lt; 12 hours." },
            { icon: Stethoscope, label: "General practitioners", body: "Send your complex headache/stroke/seizure cases for an MCI/NMC-verified written opinion before invasive workup." },
            { icon: Brain, label: "Rural &amp; tier-2/3 clinics", body: "Bring tertiary-care neurology to your district — without flying patients to Bengaluru, Delhi or Hyderabad." },
          ].map((u) => (
            <Card key={u.label} className="p-5 border-slate-200">
              <span className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 grid place-items-center">
                <u.icon className="w-5 h-5"/>
              </span>
              <h3 className="font-heading font-semibold text-slate-900 mt-3" dangerouslySetInnerHTML={{ __html: u.label }}/>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: u.body }}/>
            </Card>
          ))}
        </div>
      </section>

      {/* Social proof — "be the first" honesty placeholder */}
      <section className="max-w-6xl mx-auto px-4 pb-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">Early access</div>
              <h3 className="font-heading text-xl lg:text-2xl font-bold text-slate-900 mt-1">Be one of the first hospital partners in India.</h3>
              <p className="text-sm text-slate-600 mt-2">
                We are onboarding a small first cohort of partner hospitals across Telangana, Andhra Pradesh and Karnataka.
                Early partners get founder pricing, white-label PDF reports, and direct access to our clinical lead.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              <Card className="p-4 border-blue-100 bg-blue-50/40">
                <div className="flex items-start gap-2">
                  <span className="text-3xl text-blue-700 leading-none">&ldquo;</span>
                  <div className="text-sm text-slate-700 leading-relaxed">
                    A NIMHANS-trained second opinion in 36 hours, without flying our patient to Bangalore.
                    The structured PDF report was exactly what we needed for the patient&apos;s family.
                  </div>
                </div>
                <div className="text-[11px] font-semibold text-slate-500 mt-3">
                  — Dr. S. Iyer (referring physician) <span className="font-mono text-slate-400">· internal case feedback</span>
                </div>
              </Card>
              <Card className="p-4 border-slate-200 bg-slate-50/60">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Founder note</div>
                <div className="text-sm text-slate-700 leading-relaxed mt-2">
                  Hi — I&apos;m Dr. Sathya Reddy Kalahasti, NIMHANS-trained neurosurgeon &amp; founder of NeuroOpinion.
                  Every case from a partner hospital reaches me personally during the early-access window.
                </div>
                <a href="https://wa.me/918317674160?text=Hi%20Dr.%20Sathya%2C%20we%27re%20a%20hospital%20interested%20in%20the%20NeuroOpinion%20partner%20program."
                   target="_blank" rel="noreferrer"
                   className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-700 font-semibold hover:underline" data-testid="forhosp-founder-whatsapp">
                  Speak to me directly on WhatsApp →
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-100 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <div className="mt-8 space-y-3" data-testid="forhosp-faqs">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-xl border border-slate-200 bg-white p-4 open:shadow-sm">
                <summary className="cursor-pointer flex items-center justify-between gap-3 font-heading font-semibold text-slate-900">
                  <span>{f.q}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/hospital/signup">
              <Button className="bg-blue-700 hover:bg-blue-800 h-11 px-6" data-testid="forhosp-faq-cta">
                Apply to become a partner <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
            <div className="text-[12px] text-slate-500 mt-3">
              Or talk to us first — <a className="text-blue-700 hover:underline" href="https://wa.me/918317674160?text=Hi%2C%20we%27re%20a%20hospital%20interested%20in%20NeuroOpinion.">+91 83176 74160 on WhatsApp</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="text-2xl font-heading font-bold mt-0.5">{value}</div>
    </div>
  );
}
